import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Divider,
  Tooltip,
  Typography,
  Chip,
  MenuItem,
  Stack,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import {
  CreateInvestmentRentalPeriodRequestBody,
  InvestmentRentalPeriodDTO,
  InvestmentRentalPeriodTenantDTO,
  InvestmentRoomDTO,
  InvestmentType,
  UpdateInvestmentRentalPeriodRequestBody,
} from '@inversor-hub/types';
import EurCurrencyField from '@/components/form/EurCurrencyField';
import { RentalHistoryChart } from './RentalHistoryChart';
import {
  useCreateInvestmentRentalPeriod,
  useDeleteInvestmentRentalPeriod,
  useListInvestmentRentalPeriods,
  useUpdateInvestmentRentalPeriod,
  useListInvestmentRooms,
  useCreateInvestmentRoom,
  useGetRentReconciliation,
  useUpdateInvestmentRoom,
} from '@/lib/api/investment-rental-periods';
import { useGetMe } from '@/lib/api/me';
import { formatCurrency, formatDate, formatDateToYYYYMMDD } from '@/utils/misc';

interface RentalHistoryTabProps {
  investmentId: string;
  investmentType: InvestmentType;
}

interface RentalPeriodFormData {
  monthlyRent: number | null;
  startDate: string;
  endDate: string;
  notes: string;
  tenants: InvestmentRentalPeriodTenantDTO[];
  roomId: string;
  dueDay: number;
}

const emptyForm: RentalPeriodFormData = {
  monthlyRent: null,
  startDate: formatDateToYYYYMMDD(new Date()),
  endDate: '',
  notes: '',
  tenants: [],
  roomId: '',
  dueDay: 5,
};

interface ApiErrorResponse {
  error?: string;
}

function toFormData(period?: InvestmentRentalPeriodDTO): RentalPeriodFormData {
  if (!period) return emptyForm;

  return {
    monthlyRent: period.monthlyRent,
    startDate: formatDateToYYYYMMDD(new Date(period.startDate)),
    endDate: period.endDate ? formatDateToYYYYMMDD(new Date(period.endDate)) : '',
    notes: period.notes ?? '',
    tenants: period.tenants.map((tenant) => ({ ...tenant })),
    roomId: period.room?.id ?? '',
    dueDay: period.dueDay,
  };
}

function toRequestBody(
  data: RentalPeriodFormData,
  isRoomRent: boolean
): CreateInvestmentRentalPeriodRequestBody | UpdateInvestmentRentalPeriodRequestBody {
  return {
    monthlyRent: data.monthlyRent ?? undefined,
    startDate: data.startDate,
    endDate: data.endDate || null,
    notes: data.notes.trim() || null,
    tenants: data.tenants.map((tenant) => ({
      id: tenant.id,
      fullName: tenant.fullName.trim(),
      documentNumber: tenant.documentNumber.trim(),
    })),
    roomId: isRoomRent ? data.roomId : null,
    dueDay: data.dueDay,
  };
}

interface RentalPeriodDialogProps {
  open: boolean;
  period?: InvestmentRentalPeriodDTO;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (data: RentalPeriodFormData) => void;
  rooms: InvestmentRoomDTO[];
  existingTenants: InvestmentRentalPeriodTenantDTO[];
  isRoomRent: boolean;
}

function RentalPeriodDialog({
  open,
  period,
  isSubmitting,
  onClose,
  onSubmit,
  rooms,
  existingTenants,
  isRoomRent,
}: RentalPeriodDialogProps) {
  const [formData, setFormData] = useState<RentalPeriodFormData>(emptyForm);

  useEffect(() => {
    if (open) {
      setFormData(toFormData(period));
    }
  }, [open, period]);

  const canSubmit =
    formData.monthlyRent !== null &&
    formData.startDate.trim().length > 0 &&
    (!isRoomRent || formData.roomId.length > 0);

  const addTenant = () => {
    setFormData((prev) => ({
      ...prev,
      tenants: [...prev.tenants, { fullName: '', documentNumber: '' }],
    }));
  };

  const updateTenant = (index: number, field: 'fullName' | 'documentNumber', value: string) => {
    setFormData((prev) => ({
      ...prev,
      tenants: prev.tenants.map((tenant, tenantIndex) =>
        tenantIndex === index ? { ...tenant, [field]: value } : tenant
      ),
    }));
  };

  const removeTenant = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      tenants: prev.tenants.filter((_, tenantIndex) => tenantIndex !== index),
    }));
  };

  const tenantsAreValid = formData.tenants.every(
    (tenant) => tenant.fullName.trim() && tenant.documentNumber.trim()
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {period ? 'Editar período de alquiler' : 'Nuevo período de alquiler'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <EurCurrencyField
            label="Renta mensual"
            value={formData.monthlyRent}
            onChange={(monthlyRent) => setFormData((prev) => ({ ...prev, monthlyRent }))}
          />
          {isRoomRent && (
            <TextField
              select
              required
              label="Habitación"
              value={formData.roomId}
              onChange={(event) => setFormData((prev) => ({ ...prev, roomId: event.target.value }))}
              helperText="Cada contrato debe estar asignado a una habitación"
              fullWidth
            >
              <MenuItem value="" disabled>
                Selecciona una habitación
              </MenuItem>
              {rooms
                .filter((room) => room.active || room.id === formData.roomId)
                .map((room) => (
                  <MenuItem key={room.id} value={room.id}>
                    {room.name}
                    {room.needsReview ? ' · revisar' : ''}
                  </MenuItem>
                ))}
            </TextField>
          )}
          <TextField
            label="Día de vencimiento"
            type="number"
            value={formData.dueDay}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, dueDay: Number(event.target.value) }))
            }
            inputProps={{ min: 1, max: 28 }}
            helperText="Se usa para detectar pagos pendientes y vencidos"
            fullWidth
          />
          <TextField
            label="Desde"
            type="date"
            value={formData.startDate}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, startDate: event.target.value }))
            }
            fullWidth
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Hasta"
            type="date"
            value={formData.endDate}
            onChange={(event) => setFormData((prev) => ({ ...prev, endDate: event.target.value }))}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Déjalo vacío si es la renta vigente"
          />
          <Box>
            <Divider sx={{ mb: 1.5 }} />
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography variant="subtitle2" fontWeight={600}>
                Inquilinos
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addTenant}>
                Añadir inquilino
              </Button>
            </Box>
            {existingTenants.some(
              (tenant) => !formData.tenants.some((current) => current.id === tenant.id)
            ) && (
              <TextField
                select
                size="small"
                label="Reutilizar inquilino existente"
                value=""
                onChange={(event) => {
                  const tenant = existingTenants.find((item) => item.id === event.target.value);
                  if (tenant) {
                    setFormData((prev) => ({ ...prev, tenants: [...prev.tenants, { ...tenant }] }));
                  }
                }}
                fullWidth
                sx={{ mb: 1.5 }}
              >
                {existingTenants
                  .filter((tenant) => !formData.tenants.some((current) => current.id === tenant.id))
                  .map((tenant) => (
                    <MenuItem key={tenant.id} value={tenant.id}>
                      {tenant.fullName}
                    </MenuItem>
                  ))}
              </TextField>
            )}
            <Box display="flex" flexDirection="column" gap={1.5}>
              {formData.tenants.map((tenant, index) => (
                <Box
                  key={tenant.id ?? index}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr auto' },
                    gap: 1.5,
                    alignItems: 'center',
                  }}
                >
                  <TextField
                    label="Nombre completo"
                    size="small"
                    value={tenant.fullName}
                    onChange={(event) => updateTenant(index, 'fullName', event.target.value)}
                    required
                  />
                  <TextField
                    label="DNI/NIE"
                    size="small"
                    value={tenant.documentNumber}
                    onChange={(event) => updateTenant(index, 'documentNumber', event.target.value)}
                    required
                    sx={{ gridColumn: { xs: '1', sm: 'auto' } }}
                  />
                  <Tooltip title="Eliminar inquilino">
                    <IconButton
                      size="small"
                      onClick={() => removeTenant(index)}
                      aria-label="Eliminar inquilino"
                      sx={{ gridColumn: { xs: '2', sm: 'auto' }, gridRow: { xs: '1', sm: 'auto' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>
          <Box>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="subtitle2" fontWeight={600} mb={1.5}>
              Notas del período
            </Typography>
            <TextField
              label="Notas del período"
              value={formData.notes}
              onChange={(event) => setFormData((prev) => ({ ...prev, notes: event.target.value }))}
              multiline
              minRows={2}
              fullWidth
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!canSubmit || !tenantsAreValid || isSubmitting}
          onClick={() => onSubmit(formData)}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export const RentalHistoryTab = ({ investmentId, investmentType }: RentalHistoryTabProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { data: currentUser } = useGetMe();
  const isRoomRent = investmentType === 'ROOM_RENT';
  const { data: periods = [], isLoading } = useListInvestmentRentalPeriods(investmentId);
  const { data: rooms = [] } = useListInvestmentRooms(isRoomRent ? investmentId : undefined);
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const {
    data: reconciliation,
    isLoading: isLoadingReconciliation,
    isError: isReconciliationError,
  } = useGetRentReconciliation(investmentId, selectedMonth);
  const createMutation = useCreateInvestmentRentalPeriod(investmentId);
  const createRoomMutation = useCreateInvestmentRoom(investmentId);
  const updateRoomMutation = useUpdateInvestmentRoom(investmentId);
  const updateMutation = useUpdateInvestmentRentalPeriod(investmentId);
  const deleteMutation = useDeleteInvestmentRentalPeriod(investmentId);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<InvestmentRentalPeriodDTO>();
  const [isRoomDialogOpen, setIsRoomDialogOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [editingRoom, setEditingRoom] = useState<InvestmentRoomDTO>();
  const [roomActive, setRoomActive] = useState(true);

  const canEdit = currentUser?.role === 'investor';
  const existingTenants = useMemo(
    () => [
      ...new Map(
        periods.flatMap((period) =>
          period.tenants.flatMap((tenant) => (tenant.id ? [[tenant.id, tenant] as const] : []))
        )
      ).values(),
    ],
    [periods]
  );

  const handleCreate = () => {
    setEditingPeriod(undefined);
    setIsDialogOpen(true);
  };

  const handleEdit = (period: InvestmentRentalPeriodDTO) => {
    setEditingPeriod(period);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (data: RentalPeriodFormData) => {
    try {
      const payload = toRequestBody(data, isRoomRent);
      if (editingPeriod) {
        await updateMutation.mutateAsync({ periodId: editingPeriod.id, data: payload });
        enqueueSnackbar('Periodo de alquiler actualizado', { variant: 'success' });
      } else {
        await createMutation.mutateAsync(payload as CreateInvestmentRentalPeriodRequestBody);
        enqueueSnackbar('Periodo de alquiler creado', { variant: 'success' });
      }
      setIsDialogOpen(false);
    } catch (error) {
      const apiError = error as AxiosError<ApiErrorResponse>;
      enqueueSnackbar(
        apiError.response?.data?.error ?? 'No se pudo guardar el periodo de alquiler',
        { variant: 'error' }
      );
    }
  };

  const handleDelete = async (periodId: string) => {
    if (!window.confirm('¿Seguro que quieres eliminar este período de alquiler?')) return;

    try {
      await deleteMutation.mutateAsync(periodId);
      enqueueSnackbar('Periodo de alquiler eliminado', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('No se pudo eliminar el periodo de alquiler', { variant: 'error' });
    }
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) return;
    try {
      if (editingRoom) {
        await updateRoomMutation.mutateAsync({
          roomId: editingRoom.id,
          data: { name: newRoomName.trim(), active: roomActive },
        });
      } else {
        await createRoomMutation.mutateAsync({ name: newRoomName.trim() });
      }
      setNewRoomName('');
      setIsRoomDialogOpen(false);
      setEditingRoom(undefined);
      enqueueSnackbar(editingRoom ? 'Habitación actualizada' : 'Habitación creada', {
        variant: 'success',
      });
    } catch {
      enqueueSnackbar('No se pudo crear la habitación', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Histórico de alquileres
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Registra los importes de alquiler que ha tenido la inversión por períodos.
          </Typography>
        </Box>
        {canEdit && (
          <Stack direction="row" spacing={1}>
            {isRoomRent && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingRoom(undefined);
                  setNewRoomName('');
                  setRoomActive(true);
                  setIsRoomDialogOpen(true);
                }}
              >
                Habitación
              </Button>
            )}
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              Nuevo contrato
            </Button>
          </Stack>
        )}
      </Box>
      {isRoomRent && rooms.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2.5}>
          {rooms.map((room) => (
            <Chip
              key={room.id}
              label={`${room.name}${room.active ? '' : ' · inactiva'}`}
              color={room.needsReview ? 'warning' : 'default'}
              variant={room.active ? 'outlined' : 'filled'}
              onClick={
                canEdit
                  ? () => {
                      setEditingRoom(room);
                      setNewRoomName(room.name);
                      setRoomActive(room.active);
                      setIsRoomDialogOpen(true);
                    }
                  : undefined
              }
            />
          ))}
        </Stack>
      )}

      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: 2.5 }, mb: 3, bgcolor: 'background.default' }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Conciliación mensual
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isRoomRent
                ? 'Cobros esperados y pendientes por habitación.'
                : 'Cobros esperados y pendientes del contrato de alquiler.'}
            </Typography>
          </Box>
          <TextField
            type="month"
            size="small"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            InputProps={{ inputProps: { 'aria-label': 'Mes de conciliación' } }}
          />
        </Box>
        {isLoadingReconciliation ? (
          <Skeleton variant="rounded" height={150} />
        ) : isReconciliationError ? (
          <Alert severity="error">No se pudo cargar la conciliación de este mes.</Alert>
        ) : reconciliation ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 1.5,
                mb: 2,
              }}
            >
              {[
                ['Esperado', formatCurrency(reconciliation.totalExpected)],
                ['Cobrado', formatCurrency(reconciliation.totalPaid)],
                ['Pendiente', formatCurrency(reconciliation.totalOutstanding)],
                ['Morosos', String(reconciliation.overdueCount)],
              ].map(([label, value]) => (
                <Box key={label} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="h6" fontWeight={750}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>
            {reconciliation.items.length === 0 ? (
              <Alert severity="info">No hay cuotas esperadas para este mes.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {isRoomRent && <TableCell>Habitación</TableCell>}
                      <TableCell>Inquilino</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Cobrado / cuota</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reconciliation.items.map((item) => (
                      <TableRow key={item.charge.id}>
                        {isRoomRent && <TableCell>{item.room?.name ?? '-'}</TableCell>}
                        <TableCell>
                          {item.tenants.map((tenant) => tenant.fullName).join(', ')}
                        </TableCell>
                        <TableCell>
                          <RentStatusChip status={item.charge.status} />
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(item.charge.paidAmount)} /{' '}
                          {formatCurrency(item.charge.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        ) : null}
      </Paper>

      {isLoading ? (
        <Skeleton variant="rectangular" height={360} />
      ) : periods.length === 0 ? (
        <Alert severity="info">
          No hay histórico de alquileres. Añade el primer período para empezar el seguimiento.
        </Alert>
      ) : (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
            <RentalHistoryChart periods={periods} />
          </Paper>

          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Periodo</TableCell>
                  {isRoomRent && <TableCell>Habitación</TableCell>}
                  <TableCell align="right">Renta mensual</TableCell>
                  <TableCell>Notas</TableCell>
                  <TableCell>Inquilinos</TableCell>
                  {canEdit && <TableCell align="right">Acciones</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {periods.map((period) => (
                  <TableRow key={period.id}>
                    <TableCell>
                      {formatDate(period.startDate)} - {formatDate(period.endDate, 'Actualidad')}
                    </TableCell>
                    {isRoomRent && <TableCell>{period.room?.name ?? '-'}</TableCell>}
                    <TableCell align="right">{formatCurrency(period.monthlyRent)}</TableCell>
                    <TableCell>{period.notes || '-'}</TableCell>
                    <TableCell>
                      {period.tenants.length > 0
                        ? period.tenants.map((tenant) => tenant.fullName).join(', ')
                        : '-'}
                    </TableCell>
                    {canEdit && (
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => handleEdit(period)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton size="small" onClick={() => handleDelete(period.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <RentalPeriodDialog
        open={isDialogOpen}
        period={editingPeriod}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
        onClose={() => setIsDialogOpen(false)}
        onSubmit={handleSubmit}
        rooms={rooms}
        existingTenants={existingTenants}
        isRoomRent={isRoomRent}
      />
      <Dialog
        open={isRoomRent && isRoomDialogOpen}
        onClose={() => setIsRoomDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{editingRoom ? 'Editar habitación' : 'Nueva habitación'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Nombre"
            placeholder={`Habitación ${rooms.length + 1}`}
            value={newRoomName}
            onChange={(event) => setNewRoomName(event.target.value)}
            sx={{ mt: 1 }}
          />
          {editingRoom && (
            <FormControlLabel
              sx={{ mt: 1.5 }}
              control={
                <Switch
                  checked={roomActive}
                  onChange={(event) => setRoomActive(event.target.checked)}
                />
              }
              label="Habitación activa"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRoomDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            disabled={
              !newRoomName.trim() || createRoomMutation.isPending || updateRoomMutation.isPending
            }
            onClick={handleCreateRoom}
          >
            {editingRoom ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const statusLabels = {
  UPCOMING: 'Próximo',
  PENDING: 'Pendiente',
  PARTIAL: 'Parcial',
  PAID: 'Pagado',
  OVERDUE: 'Vencido',
} as const;

function RentStatusChip({ status }: { status: keyof typeof statusLabels }) {
  const color =
    status === 'PAID'
      ? 'success'
      : status === 'OVERDUE'
        ? 'error'
        : status === 'PARTIAL'
          ? 'warning'
          : 'default';
  return (
    <Chip
      size="small"
      label={statusLabels[status]}
      color={color}
      variant={status === 'PAID' ? 'filled' : 'outlined'}
    />
  );
}
